/**
 * Labels and tones for the CRM's fixed values (stages, lifecycles, personas,
 * …) in both languages, kept next to each other so a new enum value is one
 * edit. Page text lives in the i18n dictionaries.
 */
import { useQuery } from '@tanstack/react-query';
import type { CrmSegmentInfo, DealStage } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import type { Lang } from '../i18n';

type Labels = Record<string, { en: string; nl: string }>;

const LABELS: Record<string, Labels> = {
  stage: {
    qualified: { en: 'Qualified', nl: 'Gekwalificeerd' }, conversation: { en: 'Conversation', nl: 'Gesprek' },
    demo_scheduled: { en: 'Demo scheduled', nl: 'Demo gepland' }, demo_held: { en: 'Demo held', nl: 'Demo gehouden' },
    proposal_sent: { en: 'Proposal sent', nl: 'Voorstel verstuurd' }, won: { en: 'Won', nl: 'Gewonnen' },
    paying: { en: 'Paying', nl: 'Betalend' }, lost: { en: 'Lost', nl: 'Verloren' },
  },
  lifecycle: {
    target: { en: 'Target', nl: 'Doelwit' }, contacted: { en: 'Contacted', nl: 'Benaderd' }, engaged: { en: 'Engaged', nl: 'In gesprek' },
    opportunity: { en: 'Opportunity', nl: 'Kans' }, customer: { en: 'Customer', nl: 'Klant' }, lost: { en: 'Lost', nl: 'Verloren' },
    do_not_contact: { en: 'Do not contact', nl: 'Niet benaderen' },
  },
  segment: { ncc: { en: 'NCC', nl: 'NCC' }, airline: { en: 'Airline', nl: 'Luchtvaartmaatschappij' }, other: { en: 'Other', nl: 'Overig' } },
  operatorType: {
    aoc_airline: { en: 'AOC airline', nl: 'AOC luchtvaartmaatschappij' }, aoc_business: { en: 'AOC business aviation', nl: 'AOC zakenluchtvaart' },
    aoc_helicopter: { en: 'AOC helicopter', nl: 'AOC helikopter' }, ncc: { en: 'NCC', nl: 'NCC' }, other: { en: 'Other', nl: 'Overig' },
  },
  source: {
    icp_import: { en: 'ICP list', nl: 'ICP-lijst' }, website: { en: 'Website', nl: 'Website' }, referral: { en: 'Referral', nl: 'Doorverwijzing' },
    event: { en: 'Event', nl: 'Evenement' }, manual: { en: 'Manual', nl: 'Handmatig' },
  },
  persona: {
    safety_manager: { en: 'Safety manager', nl: 'Safety manager' }, compliance_monitoring_manager: { en: 'Compliance monitoring manager', nl: 'Compliance monitoring manager' },
    accountable_manager: { en: 'Accountable manager', nl: 'Accountable manager' }, quality_manager: { en: 'Quality manager', nl: 'Quality manager' },
    other: { en: 'Other', nl: 'Overig' },
  },
  basis: {
    legitimate_interest: { en: 'Legitimate interest', nl: 'Gerechtvaardigd belang' }, consent: { en: 'Consent', nl: 'Toestemming' },
    contract: { en: 'Contract', nl: 'Overeenkomst' },
  },
  kind: {
    note: { en: 'Note', nl: 'Notitie' }, call: { en: 'Call', nl: 'Gesprek' }, email: { en: 'Email', nl: 'E-mail' }, linkedin: { en: 'LinkedIn', nl: 'LinkedIn' },
    meeting: { en: 'Meeting', nl: 'Afspraak' }, demo: { en: 'Demo', nl: 'Demo' }, task: { en: 'Task', nl: 'Taak' },
  },
  direction: { inbound: { en: 'Inbound', nl: 'Inkomend' }, outbound: { en: 'Outbound', nl: 'Uitgaand' } },
  lostReason: {
    no_budget: { en: 'No budget', nl: 'Geen budget' }, no_need: { en: 'No need', nl: 'Geen behoefte' }, competitor: { en: 'Competitor', nl: 'Concurrent' },
    timing: { en: 'Timing', nl: 'Timing' }, no_response: { en: 'No response', nl: 'Geen reactie' }, other: { en: 'Other', nl: 'Overig' },
  },
  emailStatus: {
    candidate: { en: 'Candidate', nl: 'Kandidaat' }, pending: { en: 'Pending', nl: 'In afwachting' }, delivered: { en: 'Delivered', nl: 'Afgeleverd' },
    verified: { en: 'Verified', nl: 'Geverifieerd' }, risky: { en: 'Risky', nl: 'Onzeker' }, rejected: { en: 'Rejected', nl: 'Afgewezen' },
  },
  memberStatus: {
    pending: { en: 'Waiting', nl: 'Wacht' }, in_flight: { en: 'Mail out', nl: 'Mail onderweg' }, delivered: { en: 'Delivered', nl: 'Afgeleverd' },
    replied: { en: 'Replied', nl: 'Beantwoord' }, exhausted: { en: 'No address left', nl: 'Geen adres meer' }, skipped: { en: 'Skipped', nl: 'Overgeslagen' },
    cancelled: { en: 'Cancelled', nl: 'Gestopt' },
  },
  campaignStatus: {
    active: { en: 'Active', nl: 'Actief' }, paused: { en: 'Paused', nl: 'Gepauzeerd' }, done: { en: 'Done', nl: 'Klaar' }, cancelled: { en: 'Cancelled', nl: 'Gestopt' },
  },
  funnel: {
    qualifiedContacts: { en: 'Qualified contacts', nl: 'Gekwalificeerde contacten' }, conversations: { en: 'Conversations', nl: 'Gesprekken' },
    demos: { en: 'Demos', nl: 'Demo\'s' }, proposals: { en: 'Proposals', nl: 'Voorstellen' }, payingCustomers: { en: 'Paying customers', nl: 'Betalende klanten' },
  },
};

export type LabelGroup = keyof typeof LABELS;

export function crmLabel(group: LabelGroup, value: string | null | undefined, lang: Lang): string {
  if (!value) return '—';
  return LABELS[group]?.[value]?.[lang] ?? value;
}

/** `[{ value, label }]` for a select, in the enum's own order. */
export function crmOptions(group: LabelGroup, values: readonly string[], lang: Lang, blank?: string) {
  const opts = values.map((v) => ({ value: v, label: crmLabel(group, v, lang) }));
  return blank !== undefined ? [{ value: '', label: blank }, ...opts] : opts;
}

export function stageTone(stage: DealStage): string {
  return ({ qualified: 'neutral', conversation: 'primary', demo_scheduled: 'primary', demo_held: 'primary', proposal_sent: 'warning', won: 'success', paying: 'success', lost: 'danger' } as Record<string, string>)[stage] ?? 'neutral';
}

export function lifecycleTone(l: string): string {
  return ({ target: 'neutral', contacted: 'primary', engaged: 'primary', opportunity: 'warning', customer: 'success', lost: 'danger', do_not_contact: 'danger' } as Record<string, string>)[l] ?? 'neutral';
}

export interface Owner { userId: string; name: string; email: string }

/** Admin and staff members who can own an account or a deal. */
export function useOwners() {
  const { company } = useAuth();
  return useQuery({ queryKey: ['crm-owners', company?.id], queryFn: () => capi<Owner[]>('/crm/owners'), staleTime: 60_000 });
}

/** The built-in segments and the ones the company added (over the API or MCP). */
export function useSegments() {
  const { company } = useAuth();
  return useQuery({ queryKey: ['crm-segments', company?.id], queryFn: () => capi<CrmSegmentInfo[]>('/crm/segments'), staleTime: 60_000 });
}

/** A built-in segment in the UI language; a company's own by its name. */
export function segmentLabel(segments: CrmSegmentInfo[] | undefined, key: string | null | undefined, lang: Lang): string {
  const own = segments?.find((s) => s.key === key && !s.builtIn);
  return own ? own.name : crmLabel('segment', key, lang);
}

/** `[{ value, label }]` for a segment select; keeps `current` listed while the segments are still loading. */
export function segmentOptions(segments: CrmSegmentInfo[] | undefined, lang: Lang, blank?: string, current?: string) {
  const keys = segments?.map((s) => s.key) ?? ['ncc', 'airline', 'other'];
  if (current && !keys.includes(current)) keys.push(current);
  const opts = keys.map((k) => ({ value: k, label: segmentLabel(segments, k, lang) }));
  return blank !== undefined ? [{ value: '', label: blank }, ...opts] : opts;
}

/** Local date-time (for an <input type="datetime-local">) → ISO with offset; '' → undefined. */
export function localToIso(v: string): string | undefined {
  return v ? new Date(v).toISOString() : undefined;
}

export function emailStatusTone(s: string): string {
  return ({ candidate: 'neutral', pending: 'warning', delivered: 'primary', verified: 'success', risky: 'warning', rejected: 'danger' } as Record<string, string>)[s] ?? 'neutral';
}

export function memberTone(s: string): string {
  return ({ pending: 'neutral', in_flight: 'warning', delivered: 'primary', replied: 'success', exhausted: 'danger', skipped: 'neutral', cancelled: 'neutral' } as Record<string, string>)[s] ?? 'neutral';
}
